import React, {useEffect, useMemo, useRef} from 'react';
import {
  hospitalAdminProfileSelector,
  hospitalAdminFailureSelector,
  hospitalAdminSuccessSelector,
  hospitalAdminLoadingSelector,
} from '@/services/redux/selectors';
import {useDispatch, useSelector} from 'react-redux';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {HospitalProfileEditForm, HospitalProfileView, Specialist} from '@/components';
import {useSearchParams} from 'react-router-dom';
import {UpdateHopsitalProfileRequestPaylaod} from '@/interface';
import {updateHospitalProfileRequest, resetHospitalAdminMessage, getHospitalProfileRequest} from '@/services/redux/slices';
import {formatName, resetMessage} from '@/utils/common_functions';
import {useToast} from '@/hooks';
import {Loader2} from 'lucide-react';
import {defaultCountryId} from '@/constants';

export const HospitalProfile: React.FC = () => {
  // =======================================
  // hooks
  // =======================================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const [params, setParams] = useSearchParams();

  // =======================================
  // selectors
  // =======================================
  const isLoading = useSelector(hospitalAdminLoadingSelector);
  const hospitalAdminProfile = useSelector(hospitalAdminProfileSelector);
  const profileFailure = useSelector(hospitalAdminFailureSelector) as ErrorCodes;
  const profileSuccess = useSelector(hospitalAdminSuccessSelector) as SuccessCodes;

  // =======================================
  // state
  // =======================================
  const hasHandledInitialLoad = useRef(false);

  const isEditing = useMemo(() => {
    // if hospital admin profile is not completed, force edit mode
    if (!hospitalAdminProfile?.profile_completed) {
      return true;
    }
    return params.get('mode') === 'edit';
  }, [params, hospitalAdminProfile]);

  const profileData = useMemo((): UpdateHopsitalProfileRequestPaylaod => {
    return {
      name: hospitalAdminProfile?.name ?? '',
      email: hospitalAdminProfile?.email ?? '',
      phone: hospitalAdminProfile?.phone ?? '',
      address: hospitalAdminProfile?.address ?? '',
      city: hospitalAdminProfile?.city ?? '',
      state: hospitalAdminProfile?.state ?? '',
      country: hospitalAdminProfile?.country?.id ?? defaultCountryId,
      about: hospitalAdminProfile?.about ?? '',
      accreditation: hospitalAdminProfile?.accreditation ?? '',
      specializations: hospitalAdminProfile?.specializations?.map(specialization => specialization.id) ?? [],
      hospital_type: hospitalAdminProfile?.hospital_type?.id ?? (null as any),
      consultation_fee: hospitalAdminProfile?.consultation_fee?.amount ?? 0,
      image: hospitalAdminProfile?.image ?? '',
      fee_currency: hospitalAdminProfile?.consultation_fee?.currency?.id ?? defaultCountryId,
    };
  }, [hospitalAdminProfile]);

  // =======================================
  // functions
  // =======================================
  function handleSave(profile: UpdateHopsitalProfileRequestPaylaod) {
    const payload = new FormData();
    payload.append('name', formatName(profile.name));
    payload.append('image', profile.image);
    payload.append('consultation_fee', profile.consultation_fee.toString());
    payload.append('fee_currency', profile.fee_currency.toString());
    payload.append('address', formatName(profile.address));
    payload.append('city', formatName(profile.city));
    payload.append('state', formatName(profile.state));
    payload.append('country', profile.country.toString());
    payload.append('phone', profile.phone);
    payload.append('email', profile.email);
    payload.append('hospital_type', profile.hospital_type.toString());
    payload.append('about', profile.about);
    payload.append('accreditation', profile.accreditation);

    for (let spec of profile.specializations) {
      payload.append('specializations', spec.toString());
    }

    dispatch(updateHospitalProfileRequest(payload));
  }

  function handleEdit() {
    setParams({mode: 'edit'});
  }

  // =======================================
  // side effect
  // =======================================
  useEffect(() => {
    dispatch(getHospitalProfileRequest());
  }, []);

  useEffect(() => {
    // if hospital admin profile is not completed, set is editing to true
    if (hospitalAdminProfile && !hospitalAdminProfile.profile_completed) {
      setParams({mode: 'edit'});
    }
  }, [hospitalAdminProfile]);

  // On page refresh/mount, if profile is completed, remove edit mode from URL to go back to read-only
  useEffect(() => {
    // Only handle this once on initial load/refresh
    if (!hasHandledInitialLoad.current && hospitalAdminProfile) {
      hasHandledInitialLoad.current = true;
      // If profile is completed and URL has edit mode, clear it to go back to read-only
      if (hospitalAdminProfile.profile_completed && params.get('mode') === 'edit') {
        setParams({});
      }
    }
  }, [hospitalAdminProfile, params, setParams]);

  useEffect(() => {
    if (profileSuccess) {
      if (profileSuccess === 'S-10004') {
        showToast(getSuccessMessage(profileSuccess), 'success');
        if (hospitalAdminProfile?.profile_completed) {
          setParams({});
        } else {
          setParams({mode: 'edit'});
        }
      }
    }

    if (profileFailure) {
      showToast(getErrorMessage(profileFailure), 'failure');
    }
    return () => resetMessage(() => dispatch(resetHospitalAdminMessage()));
  }, [profileSuccess, profileFailure]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full min-h-[500px]">
        <Loader2 className="size-6 animate-spin text-ui_blue" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 flex grow">
      <div className="hospital-profile-container bg-white grow rounded-2xl border border-ui_input_border p-3 sm:p-4 md:p-6 gap-4 flex flex-col">
        {isEditing ? (
          <HospitalProfileEditForm
            profile={profileData}
            onSave={handleSave}
            lockedSpecializations={
              hospitalAdminProfile?.specializations?.filter(specialization => specialization.status).map(specialization => specialization.id) ?? []
            }
          />
        ) : (
          hospitalAdminProfile && <HospitalProfileView profileData={hospitalAdminProfile} handleEdit={handleEdit} />
        )}
      </div>
    </div>
  );
};
